import { useState, type FormEvent } from 'react'
import { Alert, Box, Button, CircularProgress, TextField, Typography } from '@mui/material'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { DASHBOARD_AUTH_PATHS, DASHBOARD_CLAIM_PREFIX, DASHBOARD_HOME } from '@yourownmetric/shared'

import { useAcceptClaimMutation, useGetClaimPreviewQuery } from '@/apis/claimApi'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { GoogleSignInButton } from '@/modules/auth/GoogleSignInButton'
import { logout, setCredentials } from '@/modules/auth/authSlice'

import { notifyOpener } from './notifyOpener'

function errorMessage(err: unknown): string | undefined {
    return (err as { data?: { error?: { message?: string } } } | undefined)?.data?.error?.message
}

const STATUS_TEXT = {
    used: 'This link was already used. Ask for a new one from the app that sent you here.',
    claimed: 'Someone already took ownership of this account. Sign in to see it.',
    expired: 'This link has expired. Links work for 10 minutes. Ask for a new one.',
} as const

/**
 * `/claim/:token`, opened (usually as a popup) by a partner app that manages this
 * analytics account. The link only says who invited whom; the person still signs in or
 * signs up here, then becomes the account owner. docs/partner-api.md §6.
 */
export function ClaimPage() {
    const { token = '' } = useParams()
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const session = useAppSelector(s => s.auth)
    const {
        data: claim,
        isLoading,
        error: previewError,
    } = useGetClaimPreviewQuery(token, {
        skip: !token,
    })
    const [accept, { isLoading: accepting, error: acceptError }] = useAcceptClaimMutation()
    const [name, setName] = useState('')
    const [password, setPassword] = useState('')
    const [done, setDone] = useState(false)

    const here = `${DASHBOARD_CLAIM_PREFIX}${encodeURIComponent(token)}`

    const finish = async (body: { token: string; name?: string; password?: string }) => {
        try {
            const res = await accept(body).unwrap()
            dispatch(setCredentials({ token: res.token, user: res.user, tenant: res.tenant }))
            setDone(true)
            if (!notifyOpener(window, res.orgId, res.returnOrigin)) {
                navigate(DASHBOARD_HOME)
            }
        } catch {
            // shown below
        }
    }

    if (!token || previewError) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'center' }}>
                <Typography variant="h4">Link not valid</Typography>
                <Typography color="text.secondary">
                    This link is incomplete or no longer exists. Ask for a new one.
                </Typography>
            </Box>
        )
    }
    if (isLoading || !claim) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
            </Box>
        )
    }

    const header = (
        <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="h4">Your analytics account</Typography>
            <Typography color="text.secondary">
                {claim.partnerName} set up <b>{claim.orgName}</b> for you and manages it. Sign in as{' '}
                <b>{claim.email}</b> to open the full dashboard. You will own the account and its
                data.
            </Typography>
        </Box>
    )
    const column = { display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 420, mx: 'auto' }

    if (done) {
        return (
            <Box sx={column}>
                {header}
                <Alert severity="success">Done. You can close this window.</Alert>
            </Box>
        )
    }
    if (claim.status !== 'pending') {
        return (
            <Box sx={column}>
                {header}
                <Alert severity="warning">{STATUS_TEXT[claim.status]}</Alert>
                <Button component={Link} to={DASHBOARD_AUTH_PATHS.login}>
                    Go to sign in
                </Button>
            </Box>
        )
    }

    const failure = acceptError && (errorMessage(acceptError) ?? 'That did not work. Try again.')

    // Signed in already.
    if (session.token && session.user) {
        const sameEmail = session.user.email.toLowerCase() === claim.email.toLowerCase()
        return (
            <Box sx={column}>
                {header}
                {failure && <Alert severity="error">{failure}</Alert>}
                {sameEmail ? (
                    <Button
                        variant="contained"
                        size="large"
                        disabled={accepting}
                        onClick={() => finish({ token })}
                    >
                        {accepting ? 'Opening…' : 'Continue'}
                    </Button>
                ) : (
                    <>
                        <Alert severity="info">
                            You're signed in as {session.user.email}. This link is for {claim.email}
                            .
                        </Alert>
                        <Button
                            variant="contained"
                            onClick={() => {
                                dispatch(logout())
                                navigate(here, { replace: true })
                            }}
                        >
                            Sign out and continue as {claim.email}
                        </Button>
                    </>
                )}
            </Box>
        )
    }

    // Has an account: sign in (password + MFA, or Google), then come back here.
    if (claim.accountExists) {
        return (
            <Box sx={column}>
                {header}
                <GoogleSignInButton text="continue_with" next={here} />
                <Button
                    variant="contained"
                    size="large"
                    component={Link}
                    to={`${DASHBOARD_AUTH_PATHS.login}?next=${encodeURIComponent(here)}`}
                >
                    Sign in with email
                </Button>
            </Box>
        )
    }

    // New here: Google, or a password account with the bound email.
    const submit = (e: FormEvent) => {
        e.preventDefault()
        void finish({ token, name, password })
    }
    return (
        <Box component="form" onSubmit={submit} sx={column}>
            {header}
            {failure && <Alert severity="error">{failure}</Alert>}
            <GoogleSignInButton text="continue_with" next={here} />
            <TextField label="Email" value={claim.email} disabled fullWidth />
            <TextField
                label="Your name"
                value={name}
                onChange={e => setName(e.target.value)}
                required
                fullWidth
                autoComplete="name"
                inputProps={{ maxLength: 100 }}
            />
            <TextField
                label="Password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                fullWidth
                autoComplete="new-password"
                inputProps={{ minLength: 8, maxLength: 128 }}
                helperText="At least 8 characters"
            />
            <Button type="submit" variant="contained" size="large" disabled={accepting} fullWidth>
                {accepting ? 'Creating your account…' : 'Create account'}
            </Button>
        </Box>
    )
}
